/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
// import { useRouter } from 'next/router';
import { ApolloProvider } from '@apollo/client';
import { SearchFormNonEditable as SearchForm } from '@marriott/mi-shop-components';
import { getDeployedEnvType, useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import operationSignatures from '../../../_constants/operationSignatures.json';
import {
  StyledTabbedSearchContainer,
  StyledTabHeader,
  StyledTab,
  StyledTabContent,
  StyledAISearchContainer,
  StyledAISearchInput,
  StyledTextInput,
  StyledSearchButton,
  StyledVoiceButton,
  StyledSuggestions,
  StyledSuggestionChip,
  StyledQuickSearchContainer,
} from './TabbedSearchContainer.styles';
import { TabbedSearchContainerProps, SearchTabType } from './TabbedSearchContainer.types';

// AI Search Tab Component
const AISearchTab: React.FC<{
  onSearch?: (query: string) => void;
  onVoiceSearch?: () => void;
  sessionData?: any;
}> = ({ onSearch, onVoiceSearch, sessionData }) => {
  const [query, setQuery] = useState('');
  // const router = useRouter();

  const handleSearch = async (searchQuery?: string) => {
    const queryToUse = searchQuery || query.trim();
    if (queryToUse) {
      try {
        const url = `/compTester?q=${encodeURIComponent(queryToUse)}&sessionId=${encodeURIComponent(
          sessionData?.sessionId || 'default-session'
        )}`;

        window.location.href = url;

        if (onSearch) {
          onSearch(queryToUse);
        }
      } catch {
        // navigation error
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const suggestions = [
    'Trending destination to celebrate 4th of July',
    "I want to try something I've never done",
    'Show me hidden gems',
    'Find me perfect trip romantic getaways',
    'Surprise me with local culture',
  ];

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };

  return (
    <StyledAISearchContainer>
      <StyledAISearchInput>
        <StyledTextInput
          type="text"
          placeholder="Try something like... 'I'm planning a golf trip to Florida with three friends in June'"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
          }}
          onKeyPress={handleKeyPress}
        />
        <StyledVoiceButton onClick={onVoiceSearch} type="button" aria-label="Voice search">
          <svg viewBox="0 0 24 24">
            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
          </svg>
        </StyledVoiceButton>
        <StyledSearchButton
          onClick={() => {
            handleSearch();
          }}
          type="button"
          disabled={!query.trim()}
          className="m-button-primary"
          style={{
            opacity: query.trim() ? 1 : 0.6,
            cursor: query.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          Find My Perfect Stay
        </StyledSearchButton>
      </StyledAISearchInput>

      <div>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>Suggested:</div>
        <StyledSuggestions>
          {suggestions.map((suggestion, index) => (
            <StyledSuggestionChip key={index} onClick={() => handleSuggestionClick(suggestion)} type="button">
              {suggestion}
            </StyledSuggestionChip>
          ))}
        </StyledSuggestions>
      </div>
    </StyledAISearchContainer>
  );
};

// Error Boundary for SearchForm
class SearchFormErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override componentDidCatch(_error: Error) {}

  override render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Simple Quick Search Fallback
const SimpleQuickSearchForm: React.FC = () => {
  return (
    <div
      style={{
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
      }}
    >
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Where can we take you?</label>
        <input
          type="text"
          placeholder="Search city, hotel, address and more..."
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '16px',
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Check In</label>
          <input
            type="date"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '16px',
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Check Out</label>
          <input
            type="date"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '16px',
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Rooms & Guests</label>
        <select
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '16px',
          }}
        >
          <option>1 Room, 2 Adults</option>
          <option>1 Room, 1 Adult</option>
          <option>2 Rooms, 4 Adults</option>
        </select>
      </div>

      <button
        style={{
          width: '100%',
          padding: '14px 24px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '16px',
          fontWeight: '500',
          cursor: 'pointer',
        }}
        onClick={() => {
          /* submit */
        }}
      >
        Find Hotels
      </button>
    </div>
  );
};

// Real SearchForm Component
const RealSearchForm: React.FC<{ sessionData?: any }> = ({ sessionData }) => {
  const MiApolloClient = useNextMiApolloClient(operationSignatures, getDeployedEnvType());

  const searchFormModel = {
    findHotelLabel: 'Find Hotels',
    placeholderTextDesktop: 'Where can we take you?',
    searchFormMobileLabel: 'Search city, hotel, address and more...',
    rooms: 'Rooms',
    children: 'Children',
    adults: 'Adults',
    guest: 'Guest',
    guests: 'Guests',
    datesPlaceholderText: 'Add Date',
    roomsAndGuestsApplyCta: 'Done',
    maxGuestPerRoom: '8',
    roomsAndGuestAdultHelperText: 'Max: 8 total guests/room',
    cancel: 'Cancel',
    add: 'Add',
    remove: 'Remove',
    ageLabel: 'Age',
    maxChildAgeLabel: '17',
    enableFlexibleSearch: 'true',
    flexibleDates: 'Flexible Dates',
    exactDatesTabText: 'Specific Dates',
    flexibleTabText: 'Flexible Dates',
    specialRatesPoints: 'Special Rates/Points',
    usePointsCertificates: 'Use Points / Awards',
    enableRedeemPoints: true,
    months: 'January,February,March,April,May,June,July,August,September,October,November,December',
    weekdays: 'S,M,T,W,T,F,S',
    weekdaysShort: 'Sun,Mon,Tue,Wed,Thu,Fri,Sat',
    weekdaysLong: 'Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday',
    night: 'Night',
    nights: 'Nights',
    checkInLabel: 'CHECK IN',
    checkOutLabel: 'CHECK OUT',
    dateModuleEyebrowText: 'Dates',
    roomsAndGuestEyebrowText: 'Rooms and Guests',
    enterCodeEyebrowText: 'ENTER CODE',
    addCorpOrPromoCode: 'Enter Code',
    localDateFormat: 'MM/DD/YYYY',
    ':type': 'mi-aem-shop-spa/components/content/homepagesearch/v1/homepagesearch',
  };

  return (
    <ApolloProvider client={MiApolloClient}>
      <SearchForm model={searchFormModel} sessionData={sessionData || {}} />
    </ApolloProvider>
  );
};

// Quick Search Tab Component
const QuickSearchTab: React.FC<{ sessionData?: any }> = ({ sessionData }) => {
  return (
    <StyledQuickSearchContainer>
      <SearchFormErrorBoundary fallback={<SimpleQuickSearchForm />}>
        <RealSearchForm sessionData={sessionData} />
      </SearchFormErrorBoundary>
    </StyledQuickSearchContainer>
  );
};

// Main Tabbed Search Container
export const TabbedSearchContainer: React.FC<TabbedSearchContainerProps> = ({ className, sessionData, onAISearch }) => {
  const [activeTab, setActiveTab] = useState<SearchTabType>('ai');
  const handleVoiceSearch = () => {};

  return (
    <StyledTabbedSearchContainer className={className} data-component-name="o-chat-TabbedSearchContainer">
      <StyledTabHeader>
        <StyledTab $isActive={activeTab === 'ai'} onClick={() => setActiveTab('ai')} type="button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          Search with AI
        </StyledTab>
        <StyledTab $isActive={activeTab === 'quick'} onClick={() => setActiveTab('quick')} type="button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          Quick Search
        </StyledTab>
      </StyledTabHeader>

      <StyledTabContent>
        {activeTab === 'ai' ? (
          <AISearchTab onSearch={onAISearch} onVoiceSearch={handleVoiceSearch} sessionData={sessionData} />
        ) : (
          <QuickSearchTab sessionData={sessionData} />
        )}
      </StyledTabContent>
    </StyledTabbedSearchContainer>
  );
};

export default TabbedSearchContainer;
